'use client';

import { Button, Typography } from '@musinsa/mds';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import { Controller, useFormContext } from 'react-hook-form';
import { ClearableTextField } from '../../../common/components/ClearableTextField';
import { AccountInfoView } from './AccountInfoView';
import { BankSelectField } from './BankSelectField';
import { useSettlementFormContext } from './SettlementFormContext';
import type { SettlementFormData } from './types';

interface AccountDisplayInfo {
  bankName?: string;
  accountNumber?: string;
  accountHolder?: string;
}

interface AccountInfoSectionProps {
  isViewMode?: boolean;
  accountDisplayInfo?: AccountDisplayInfo;
  onRequestAccountEdit: () => void;
  onCancelAccountEdit?: () => void;
  canToggleAccountView?: boolean;
}

export const AccountInfoSection = ({
  isViewMode = false,
  accountDisplayInfo,
  onRequestAccountEdit,
  onCancelAccountEdit,
  canToggleAccountView = false,
}: AccountInfoSectionProps) => {
  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext<SettlementFormData>();
  const { service } = useDeviceInfo();

  const { accountValidation, handlers } = useSettlementFormContext();

  const renderHeader = () => (
    <div className="flex items-center justify-between py-2">
      <Typography variant="body14pxMed">계좌 정보</Typography>

      {canToggleAccountView && (
        <Button
          variant="secondary"
          size={24}
          type="button"
          onClick={isViewMode ? onRequestAccountEdit : onCancelAccountEdit}
        >
          <Typography variant="body13pxMed">{isViewMode ? '계좌 수정' : '취소'}</Typography>
        </Button>
      )}
    </div>
  );

  if (isViewMode) {
    return (
      <div className="flex flex-col">
        {renderHeader()}

        <AccountInfoView {...accountDisplayInfo} />
      </div>
    );
  }
  const businessType = watch('businessType');

  return (
    <div className="flex flex-col py-2">
      {renderHeader()}

      <Controller
        name="bankName"
        control={control}
        render={({ field }) => (
          <BankSelectField
            value={field.value || ''}
            onChange={handlers.onAccountFieldChange('bankName', field.onChange)}
            error={!!errors.bankName}
            errorText={errors.bankName?.message}
          />
        )}
      />

      <div className="flex flex-col gap-2">
        <Controller
          name="accountNumber"
          control={control}
          render={({ field }) => (
            <ClearableTextField
              value={field.value || ''}
              onChange={(e) => {
                // 숫자만 허용
                const value = e.target.value.replace(/[^0-9]/g, '');
                const syntheticEvent = {
                  ...e,
                  target: { ...e.target, value },
                };
                handlers.onAccountInputChange('accountNumber', field.onChange)(syntheticEvent);
              }}
              placeholder="계좌번호를 입력해주세요"
              error={!!errors.accountNumber}
              errorText={errors.accountNumber?.message}
              inputMode="numeric"
              pattern="[0-9]*"
            />
          )}
        />
      </div>

      <div className="flex flex-col">
        <div className="flex gap-[4px] items-start">
          <Controller
            name="accountHolder"
            control={control}
            render={({ field }) => (
              <ClearableTextField
                value={field.value || ''}
                onChange={handlers.onAccountInputChange('accountHolder', field.onChange)}
                error={!!errors.accountHolder}
                errorText={errors.accountHolder?.message}
                placeholder="예금주를 입력해주세요"
                className="w-full"
                containerClassName="py-0 grow"
              />
            )}
          />

          {businessType === 'NATURAL_PERSON' && (
            <Button
              variant="secondary"
              size={36}
              type="button"
              onClick={accountValidation.onVerify}
              disabled={accountValidation.isValidating}
              className="shrink-0"
            >
              <Typography variant="body13pxMed">계좌 인증 하기</Typography>
            </Button>
          )}
        </div>

        {accountValidation.error && (
          <Typography variant="textEtc11pxReg" colorScheme="red" className="pt-2">
            {accountValidation.error}
          </Typography>
        )}

        {!isViewMode && (
          <Typography
            variant="textEtc11pxReg"
            colorScheme="gray-500"
            wordBreak="breakWords"
            className="pt-2"
          >
            {businessType === 'NATURAL_PERSON'
              ? '예금주는 큐레이터 회원명과 일치해야 합니다.'
              : `${service === SERVICE.MUSINSA ? '무신사' : '29CM'} 스토어에 등록된 계정의 실명과 사업자 등록증 상의 대표자 명과 일치해야 합니다.`}
          </Typography>
        )}
      </div>
    </div>
  );
};
